import type { SahiConfig } from "@/core/cameraApi/schemas/nn";
import { DEFAULT_SAHI_CONFIG } from "@/core/cameraApi/schemas/nn";
import { NumberInput } from "@/modules/shadcn/ui/number-input";
import { Slider } from "@/modules/shadcn/ui/slider";
import { Switch } from "@/modules/shadcn/ui/switch";
import { useTranslation } from "react-i18next";

interface SahiConfigPanelProps {
  value: SahiConfig | null;
  onChange: (config: SahiConfig | null) => void;
}

export const SahiConfigPanel = ({ value, onChange }: SahiConfigPanelProps) => {
  const { t } = useTranslation(["run", "common"]);
  const enabled = value !== null;
  const config = value ?? DEFAULT_SAHI_CONFIG;

  const handleToggle = (checked: boolean) => {
    onChange(checked ? DEFAULT_SAHI_CONFIG : null);
  };

  const updateField = (field: keyof SahiConfig, v: number) => {
    onChange({ ...config, [field]: v });
  };

  return (
    <section className="flex flex-col gap-4 border-b px-6 py-5 last:border-b-0">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("sahi.title")}
        </h2>
        <Switch checked={enabled} onCheckedChange={handleToggle} size="sm" />
      </div>
      {enabled && (
        <div className="flex flex-col gap-4">
          <SahiField
            label={t("sahi.sliceWidth")}
            value={config.slice_width}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => updateField("slice_width", v)}
          />
          <SahiField
            label={t("sahi.sliceHeight")}
            value={config.slice_height}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => updateField("slice_height", v)}
          />
          <SahiField
            label={t("sahi.overlapRatio")}
            value={config.overlap_ratio}
            min={0}
            max={0.5}
            step={0.01}
            onChange={(v) => updateField("overlap_ratio", v)}
          />
          <SahiField
            label={t("sahi.nmsIouThreshold")}
            value={config.nms_iou_threshold}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => updateField("nms_iou_threshold", v)}
          />
        </div>
      )}
    </section>
  );
};

const SahiField = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <label className="text-sm text-muted-foreground">{label}</label>
      <NumberInput
        decimal
        value={value}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => {
          if (v !== null) onChange(Math.min(max, Math.max(min, v)));
        }}
        className="h-8 w-20 text-right text-sm"
      />
    </div>
    <Slider
      value={value}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v as number)}
    />
  </div>
);
